<script lang="ts">
  /**
   * iOS "Add to Home Screen" helper banner.
   *
   * iOS Safari exposes no programmatic install (no `beforeinstallprompt`), so the
   * only path is Share → Add to Home Screen. This banner nudges the student through
   * those two steps when running iOS Safari and NOT already installed/standalone.
   * Dismissal is remembered via localStorage so it shows at most until accepted.
   */
  import { onMount } from "svelte";
  import { _ } from "./i18n";

  const DISMISS_KEY = "student-install-dismissed";

  let show = false;

  function isIos(): boolean {
    const ua = navigator.userAgent;
    if (/iphone|ipad|ipod/i.test(ua)) return true;
    // iPadOS 13+ reports as desktop Safari ("MacIntel") but is touch-capable.
    return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  }

  function isStandalone(): boolean {
    const nav = navigator as Navigator & { standalone?: boolean };
    return (
      nav.standalone === true ||
      (typeof window.matchMedia === "function" &&
        window.matchMedia("(display-mode: standalone)").matches)
    );
  }

  function dismissed(): boolean {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  }

  function dismiss(): void {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Ignore storage failures (private mode); just hide for this session.
    }
    show = false;
  }

  onMount(() => {
    show = isIos() && !isStandalone() && !dismissed();
  });
</script>

{#if show}
  <div class="install" role="dialog" aria-label={$_("install.title")} data-testid="install-prompt">
    <div class="row">
      <span class="icon" aria-hidden="true">
        <!-- iOS Share glyph: a square box with an upward arrow. -->
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3v12" />
          <path d="M8 7l4-4 4 4" />
          <path d="M6 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1" />
        </svg>
      </span>
      <div class="body">
        <p class="title">{$_("install.title")}</p>
        <p class="step">{$_("install.step1")}</p>
        <p class="step">{$_("install.step2")}</p>
      </div>
      <button
        class="close"
        type="button"
        on:click={dismiss}
        aria-label={$_("install.dismiss")}
        data-testid="install-dismiss"
      >
        ×
      </button>
    </div>
    <button class="gotit" type="button" on:click={dismiss} data-testid="install-gotit">
      {$_("install.gotIt")}
    </button>
  </div>
{/if}

<style>
  .install {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 50;
    padding: 0.875rem 1rem calc(0.875rem + env(safe-area-inset-bottom));
    background: #1e3a8a;
    color: #eef2ff;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.35);
  }
  .row {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .icon {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }
  .body {
    flex: 1 1 auto;
    min-width: 0;
  }
  .title {
    margin: 0 0 0.25rem;
    font-weight: 700;
    font-size: 1rem;
  }
  .step {
    margin: 0.1rem 0;
    font-size: 0.875rem;
    color: #dbe3ff;
    line-height: 1.3;
  }
  .close {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #eef2ff;
    font-size: 1.25rem;
    line-height: 1;
    display: grid;
    place-items: center;
  }
  .gotit {
    margin-top: 0.75rem;
    width: 100%;
    padding: 0.55rem 0.75rem;
    border: none;
    border-radius: 8px;
    background: #ffffff;
    color: #1e3a8a;
    font-weight: 700;
    font-size: 0.95rem;
  }
</style>
